import React, { useState, useEffect, useCallback } from 'react';
import { adminService } from '../services/api';
import './EcosystemPanels.css';

const OAUTH_SCOPES = [
  'https://www.googleapis.com/auth/webmasters.readonly',
  'https://www.googleapis.com/auth/analytics.readonly',
];

const OAUTH_FIX_STEPS = [
  'Google Cloud → proyecto Javiland Panel → credenciales OAuth (Client ID + Secret nuevo si hace falta).',
  'Habilitar APIs: Search Console + Google Analytics Data API.',
  'OAuth Playground → ⚙️ Use your own OAuth credentials → pegar ID y Secret.',
  `Step 1: autorizar scopes webmasters.readonly + analytics.readonly.`,
  'Step 2: Exchange → copiar Refresh token (empieza por 1//).',
  'Hostinger → public_html/api/.env → GSC_CLIENT_ID, GSC_CLIENT_SECRET, GSC_REFRESH_TOKEN.',
  'Verificar GSC_SITE_URL=sc-domain:javiland.com y GA4_PROPERTY_ID=541343523.',
  'Probar con test-google-oauth.php en el servidor → borrar el archivo.',
];

const MarketingHub = () => {
  const [gsc, setGsc] = useState(null);
  const [ga4, setGa4] = useState(null);
  const [oauthStatus, setOauthStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [gscError, setGscError] = useState(null);
  const [ga4Error, setGa4Error] = useState(null);

  const loadMetrics = useCallback(async () => {
    setLoading(true);
    setGscError(null);
    setGa4Error(null);

    const [statusResult, gscResult, ga4Result] = await Promise.allSettled([
      adminService.getGoogleMarketingStatus(),
      adminService.getSearchConsoleSummary(),
      adminService.getGa4Summary(),
    ]);

    if (statusResult.status === 'fulfilled') {
      setOauthStatus(statusResult.value);
    } else {
      setOauthStatus(null);
    }

    if (gscResult.status === 'fulfilled') {
      setGsc(gscResult.value);
    } else {
      setGsc(null);
      setGscError(gscResult.reason?.response?.data?.message || 'No se pudo cargar Search Console');
    }

    if (ga4Result.status === 'fulfilled') {
      setGa4(ga4Result.value);
    } else {
      setGa4(null);
      setGa4Error(ga4Result.reason?.response?.data?.message || 'No se pudo cargar GA4');
    }

    setLoading(false);
  }, []);

  useEffect(() => {
    loadMetrics();
  }, [loadMetrics]);

  const oauthError = gsc?.oauth_error || ga4?.oauth_error || oauthStatus?.oauth_error;
  const oauthBroken = oauthStatus?.token_ok === false || oauthError === 'invalid_grant' || oauthError === 'invalid_client' || oauthError === 'unauthorized_client';
  const showOAuthFix = oauthBroken || (gsc && !gsc.configured) || (ga4 && !ga4.configured);

  return (
    <div className="eco-panel">
      <header className="eco-panel-header eco-marketing-header">
        <div>
          <h2>Marketing y tráfico</h2>
          <p>Search Console y GA4 en vivo · Plan alineado con Notion (PROC-MKT-002).</p>
        </div>
        <button type="button" className="eco-btn eco-btn--ghost" onClick={loadMetrics} disabled={loading}>
          <i className={`fas fa-sync-alt${loading ? ' fa-spin' : ''}`} aria-hidden /> Actualizar métricas
        </button>
      </header>

      {showOAuthFix ? (
        <section className="eco-oauth-banner" aria-label="Configuración OAuth Google">
          <h3>
            <i className="fas fa-key" aria-hidden /> Conexión Google pendiente
          </h3>
          <p className="eco-gsc-warn">
            {oauthStatus?.hint ||
              gsc?.message ||
              ga4?.message ||
              'Regenera el refresh token en Hostinger (api/.env).'}
            {oauthError ? ` (${oauthError})` : ''}
          </p>
          <p className="eco-oauth-note">
            GSC y GA4 comparten el mismo token OAuth. Si uno falla, los dos fallan hasta actualizar{' '}
            <code>GSC_REFRESH_TOKEN</code>.
          </p>
          <ol className="eco-setup-steps">
            {OAUTH_FIX_STEPS.map((step) => (
              <li key={step}>{step}</li>
            ))}
          </ol>
          <details className="eco-oauth-scopes">
            <summary>Scopes para OAuth Playground</summary>
            <pre>{OAUTH_SCOPES.join('\n')}</pre>
          </details>
          <p className="eco-card-notes">
            Guía completa en repo: <code>GUIA_MARKETING_PANEL_GSC_GA4.md</code>
          </p>
          <div className="eco-oauth-links">
            <a
              href="https://developers.google.com/oauthplayground/"
              target="_blank"
              rel="noopener noreferrer"
              className="eco-ext-link"
            >
              OAuth Playground
            </a>
            <a
              href="https://console.cloud.google.com/apis/credentials"
              target="_blank"
              rel="noopener noreferrer"
              className="eco-ext-link"
            >
              Google Cloud Credentials
            </a>
          </div>
        </section>
      ) : null}

      {oauthStatus?.token_ok ? (
        <p className="eco-oauth-ok">
          <i className="fas fa-check-circle" aria-hidden /> OAuth Google OK
          {oauthStatus.gsc_site ? ` · ${oauthStatus.gsc_site}` : ''}
          {oauthStatus.ga4_property_set ? ' · GA4 configurado' : ' · Falta GA4_PROPERTY_ID'}
        </p>
      ) : null}

      <section className="eco-marketing-grid">
        <div className="eco-marketing-card">
          <h3>
            <i className="fab fa-google" aria-hidden /> Google Search Console
          </h3>
          {loading ? <p>Cargando métricas…</p> : null}
          {gscError ? <p className="eco-gsc-warn">{gscError}</p> : null}
          {gsc?.configured && gsc.summary ? (
            <>
              {gsc.period ? (
                <p className="eco-metric-period">
                  {gsc.period.start} → {gsc.period.end} ({gsc.period.days} días)
                </p>
              ) : null}
              <ul className="eco-gsc-metrics">
                <li>
                  <span>Clics</span>
                  <strong>{gsc.summary.clicks ?? '—'}</strong>
                </li>
                <li>
                  <span>Impresiones</span>
                  <strong>{gsc.summary.impressions ?? '—'}</strong>
                </li>
                <li>
                  <span>CTR</span>
                  <strong>
                    {gsc.summary.ctr != null ? `${(gsc.summary.ctr * 100).toFixed(1)}%` : '—'}
                  </strong>
                </li>
                <li>
                  <span>Posición media</span>
                  <strong>
                    {gsc.summary.position != null ? gsc.summary.position.toFixed(1) : '—'}
                  </strong>
                </li>
              </ul>
              {gsc.topQueries?.length > 0 ? (
                <div className="eco-top-queries">
                  <h4>Top consultas</h4>
                  <table>
                    <thead>
                      <tr>
                        <th>Consulta</th>
                        <th>Clics</th>
                        <th>Imp.</th>
                      </tr>
                    </thead>
                    <tbody>
                      {gsc.topQueries.map((row) => (
                        <tr key={row.query}>
                          <td>{row.query}</td>
                          <td>{row.clicks}</td>
                          <td>{row.impressions}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : null}
            </>
          ) : null}
          {!loading && !gscError && gsc && !gsc.configured ? (
            <p className="eco-gsc-warn">
              {gsc.message || 'Search Console no configurado en servidor.'}
            </p>
          ) : null}
          <a
            href="https://search.google.com/search-console"
            target="_blank"
            rel="noopener noreferrer"
            className="eco-ext-link"
          >
            Abrir Search Console
          </a>
        </div>

        <div className="eco-marketing-card">
          <h3>
            <i className="fas fa-chart-line" aria-hidden /> Google Analytics 4
          </h3>
          {loading ? <p>Cargando métricas…</p> : null}
          {ga4Error ? <p className="eco-gsc-warn">{ga4Error}</p> : null}
          {ga4?.configured && ga4.summary ? (
            <>
              {ga4.period ? (
                <p className="eco-metric-period">
                  {ga4.period.start} → {ga4.period.end} ({ga4.period.days} días)
                </p>
              ) : null}
              <ul className="eco-gsc-metrics">
                <li>
                  <span>Sesiones</span>
                  <strong>{ga4.summary.sessions ?? '—'}</strong>
                </li>
                <li>
                  <span>Usuarios activos</span>
                  <strong>{ga4.summary.activeUsers ?? '—'}</strong>
                </li>
                <li>
                  <span>Vistas de página</span>
                  <strong>{ga4.summary.pageViews ?? '—'}</strong>
                </li>
              </ul>
            </>
          ) : null}
          {!loading && ga4 && !ga4.configured ? (
            <p className="eco-gsc-warn">{ga4.message || 'GA4 no configurado en servidor.'}</p>
          ) : null}
          <p className="eco-card-notes">
            Tag sitio público: <code>G-X0J6B7QQX2</code> en <code>frontend/.env.production</code>
          </p>
          <a
            href="https://analytics.google.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="eco-ext-link"
          >
            Abrir Google Analytics
          </a>
        </div>

        <div className="eco-marketing-card eco-marketing-card--muted">
          <h3>
            <i className="fas fa-tasks" aria-hidden /> Plan marketing
          </h3>
          <ul className="eco-plan-list">
            <li>
              <strong>Ahora:</strong> cerrar OAuth → ver números en este panel
            </li>
            <li>Notion: backlog diseño (65 propuestas) — PROC-MKT-002</li>
            <li>SEO sitelinks: Tienda · Servicios · Growth Partner · Conoce Colombia</li>
            <li>Después: tráfico a combos, Aleja (Conoce Colombia), bienestar</li>
          </ul>
          <p className="eco-card-notes">
            Docs: GUIA_MARKETING_PANEL_GSC_GA4.md · BACKLOG_MEJORAS_DISENO.md
          </p>
        </div>
      </section>
    </div>
  );
};

export default MarketingHub;
